import { useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import type {
  CalendarColorBy,
  CalendarDisplayPrefs,
  CalendarWeekStart,
} from '@/store/CalendarDisplayStore'
import type { CalendarShowBy } from './calendarLayout'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { useLocale } from '@/lib/i18n/LocaleProvider'
import useCalendarDisplayStore, { useCalendarDisplay } from '@/store/CalendarDisplayStore'

interface CalendarDisplaySectionProps {
  /** The project or space id the options are remembered for. */
  entityId: string
}

/**
 * Calendar display options (design 26b). Self-contained: mount it inside the
 * header's Display popover. Renders two blocks meant to sit in the popover's
 * `divide-y` list. "Show completed" is left to the popover's shared row
 * (HideCompletedStore). Reset with {@link useResetCalendarDisplay}.
 */
export function CalendarDisplaySection({ entityId }: CalendarDisplaySectionProps) {
  const { t } = useTranslation('projects')
  const prefs = useCalendarDisplay(entityId)
  const setPref = useCalendarDisplayStore((s) => s.setPref)
  const { weekStartsOn: userWeekStart } = useLocale()

  const set = <TKey extends keyof CalendarDisplayPrefs>(key: TKey, value: CalendarDisplayPrefs[TKey]) =>
    setPref(entityId, key, value)

  const weekStart: 0 | 1 = prefs.weekStartsOn ?? (userWeekStart === 1 ? 1 : 0)

  return (
    <>
      <div className="space-y-0.5 px-4 py-2.5">
        <Row label={t('calendar.display.showBy')}>
          <Pill<CalendarShowBy>
            value={prefs.showBy}
            onChange={(v) => set('showBy', v)}
            options={[
              { value: 'due', label: t('calendar.display.showByDue') },
              { value: 'range', label: t('calendar.display.showByRange') },
            ]}
          />
        </Row>
        <Row label={t('calendar.display.colorBy')}>
          <Choice<CalendarColorBy>
            value={prefs.colorBy}
            onChange={(v) => set('colorBy', v)}
            options={[
              { value: 'project', label: t('calendar.display.colorProject') },
              { value: 'status', label: t('calendar.display.colorStatus') },
              { value: 'priority', label: t('calendar.display.colorPriority') },
            ]}
          />
        </Row>
        <Row label={t('calendar.display.weekStarts')}>
          <Choice<'0' | '1'>
            value={String(weekStart) as '0' | '1'}
            onChange={(v) => set('weekStartsOn', Number(v) as CalendarWeekStart)}
            options={[
              { value: '0', label: t('calendar.display.sunday') },
              { value: '1', label: t('calendar.display.monday') },
            ]}
          />
        </Row>
      </div>
      <div className="space-y-0.5 px-4 py-2.5">
        <Row label={t('calendar.display.showWeekends')}>
          <Switch
            checked={prefs.showWeekends}
            onCheckedChange={(v) => set('showWeekends', v)}
            aria-label={t('calendar.display.showWeekends')}
          />
        </Row>
        <Row label={t('calendar.display.unscheduledTray')}>
          <Switch
            checked={prefs.unscheduledTray}
            onCheckedChange={(v) => set('unscheduledTray', v)}
            aria-label={t('calendar.display.unscheduledTray')}
          />
        </Row>
      </div>
    </>
  )
}

/** Reset handler for the Display popover's footer ("Reset · Only this view"). */
export function useResetCalendarDisplay(entityId: string): () => void {
  const reset = useCalendarDisplayStore((s) => s.reset)
  return () => reset(entityId)
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-8 items-center justify-between gap-3">
      <span className="text-[13px] text-foreground/80">{label}</span>
      {children}
    </div>
  )
}

interface OptionProps<T extends string> {
  value: T
  onChange: (value: T) => void
  options: Array<{ value: T; label: string }>
}

/** Small segmented pill (design 26b "Due date | Start → due"). */
function Pill<T extends string>({ value, onChange, options }: OptionProps<T>) {
  return (
    <div role="radiogroup" className="flex items-center gap-0.5 rounded-lg bg-secondary p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-md px-2 py-0.5 text-[12px] font-medium transition-colors',
            value === o.value ? 'bg-card text-foreground shadow-xs' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/** Value with a chevron that opens a short list, current value ticked. */
function Choice<T extends string>({ value, onChange, options }: OptionProps<T>) {
  const [open, setOpen] = useState(false)
  const selected = options.find((o) => o.value === value)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[13px] font-medium text-foreground hover:bg-secondary"
        >
          {selected?.label ?? value}
          <ChevronDown className="size-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-1" align="end">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            onClick={() => {
              onChange(o.value)
              setOpen(false)
            }}
          >
            {o.label}
            {o.value === value && <Check className="size-3.5" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}
